import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  fetchBreakdown,
  fetchDaily,
  fetchHourly,
  fetchPace,
  fetchPlacements,
  fetchRunRate,
} from "@/components/SnippetRevenue/snippetRevenueServices";
import { isSnippetAdmin } from "@/composables/useSnippetApiToken";

const ADMIN_ONLY_DIMENSIONS = ["buyer", "show"];

export const BREAKDOWN_DIMENSIONS = ["domain", "geo", "buyer", "show"];

const DEFAULT_RANGE_DAYS = 7;

const toDayString = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const defaultRange = () => {
  const today = new Date();
  const from = new Date();
  from.setDate(from.getDate() - DEFAULT_RANGE_DAYS);
  return [from, today];
};

const emptyTotals = () => ({ loads: 0, impressions: 0, revenue: 0 });

export const useSnippetRevenueStore = defineStore("snippetRevenue", () => {
  const dateRange = ref(defaultRange());
  const memberFilter = ref({ partners: [], groups: [], sellers: [] });
  const viewAsSellerId = ref(null);
  const placements = ref([]);
  const loading = ref({});

  const pace = ref({
    today: emptyTotals(),
    yesterday: emptyTotals(),
    change: { loads: null, impressions: null, revenue: null },
  });
  const runRate = ref({
    revenueThisMonth: 0,
    averageDailyRevenue: 0,
    forecastedRevenue: 0,
    mrr: 0,
  });
  const hourly = ref({ today: [], yesterday: [] });
  const breakdown = ref({ domain: [], geo: [], buyer: [], show: [] });
  const daily = ref({
    rows: [],
    totalRecords: 0,
    // The server decides the column set per viewer, so there is no safe default —
    // guessing one shows a publisher headers they should not have if the fetch fails.
    columns: [],
  });
  const dailyParams = ref(null);

  const dimensions = computed(() =>
    isSnippetAdmin()
      ? BREAKDOWN_DIMENSIONS
      : BREAKDOWN_DIMENSIONS.filter(
          (dimension) => !ADMIN_ONLY_DIMENSIONS.includes(dimension)
        )
  );

  const filterParam = computed(() => ({
    memberFilter: JSON.stringify(memberFilter.value),
    ...(viewAsSellerId.value ? { asSellerId: viewAsSellerId.value } : {}),
  }));

  const rangeParams = computed(() => ({
    startDate: toDayString(dateRange.value[0]),
    endDate: toDayString(dateRange.value[1] ?? dateRange.value[0]),
    ...filterParam.value,
  }));

  const isLoading = (key) => (loading.value[key] ?? 0) > 0;

  const isBusy = computed(() =>
    Object.values(loading.value).some((count) => count > 0)
  );

  const track = async (key, run) => {
    loading.value = { ...loading.value, [key]: (loading.value[key] ?? 0) + 1 };
    try {
      return await run();
    } finally {
      loading.value = { ...loading.value, [key]: loading.value[key] - 1 };
    }
  };

  const loadPlacements = () =>
    track("placements", async () => {
      const data = await fetchPlacements({
        startDate: toDayString(dateRange.value[0]),
        endDate: toDayString(dateRange.value[1] ?? dateRange.value[0]),
        ...(viewAsSellerId.value ? { asSellerId: viewAsSellerId.value } : {}),
      });
      if (data) placements.value = data;
    });

  const loadPace = () =>
    track("pace", async () => {
      const data = await fetchPace(filterParam.value);
      if (data) pace.value = data;
    });

  const loadRunRate = () =>
    track("runRate", async () => {
      const data = await fetchRunRate(filterParam.value);
      if (data) runRate.value = data;
    });

  const loadHourly = () =>
    track("hourly", async () => {
      const data = await fetchHourly(filterParam.value);
      if (data) hourly.value = data;
    });

  const loadBreakdown = (dimension) =>
    track(`breakdown:${dimension}`, async () => {
      const data = await fetchBreakdown({
        ...rangeParams.value,
        dimension,
      });
      if (data) breakdown.value = { ...breakdown.value, [dimension]: data };
    });

  const loadDaily = (options = {}) =>
    track("daily", async () => {
      const params = { ...rangeParams.value, ...options };
      const data = await fetchDaily(params);
      if (data) {
        daily.value = data;
        dailyParams.value = params;
      }
    });

  const exportDaily = () =>
    dailyParams.value
      ? fetchDaily({ ...dailyParams.value, page: 0, rows: 200 })
      : Promise.resolve(null);

  const setDateRange = (value) => {
    dateRange.value = value;
  };

  const setViewAsSellerId = (value) => {
    viewAsSellerId.value = value || null;
  };

  const setMemberFilter = (value) => {
    memberFilter.value = { ...memberFilter.value, ...value };
  };

  const loadToday = () =>
    Promise.all([loadPace(), loadRunRate(), loadHourly()]);

  const loadRange = () =>
    Promise.all([
      loadDaily(),
      ...dimensions.value.map((dimension) => loadBreakdown(dimension)),
    ]);

  return {
    dateRange,
    memberFilter,
    viewAsSellerId,
    placements,
    pace,
    runRate,
    hourly,
    breakdown,
    daily,
    dimensions,
    isBusy,
    isLoading,
    setDateRange,
    setMemberFilter,
    setViewAsSellerId,
    loadPlacements,
    loadToday,
    loadRange,
    loadDaily,
    exportDaily,
  };
});
